import { useState } from "react";
import type { ContentSelection, TimeRange } from "../../../convex/lib/quote";

const CONTENT: { key: keyof ContentSelection; label: string; tint: string }[] = [
  { key: "text", label: "Text", tint: "var(--ink-dim)" },
  { key: "pics", label: "Pictures", tint: "var(--slack)" },
  { key: "video", label: "Video", tint: "var(--discord)" },
  { key: "files", label: "Files", tint: "var(--live)" },
];

export function ContentToggles({
  value,
  onChange,
}: {
  value: ContentSelection;
  onChange: (v: ContentSelection) => void;
}) {
  return (
    <div className="toggle-row" role="group" aria-label="Content types">
      {CONTENT.map((c) => {
        const on = value[c.key];
        return (
          <button
            key={c.key}
            type="button"
            className="chip-toggle"
            aria-pressed={on}
            onClick={() => onChange({ ...value, [c.key]: !on })}
          >
            <span className="chip-dot" style={{ background: on ? c.tint : "var(--line-strong)" }} />
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

type RangeKey = "all" | "last1y" | "last6mo" | "last90d" | "lastN";

const RANGES: { key: RangeKey; label: string }[] = [
  { key: "all", label: "All time" },
  { key: "last1y", label: "Last 1yr" },
  { key: "last6mo", label: "Last 6mo" },
  { key: "last90d", label: "Last 90d" },
  { key: "lastN", label: "Recent messages" },
];

function rangeKey(r: TimeRange): RangeKey {
  if (r.kind === "all") return "all";
  if (r.kind === "lastN") return "lastN";
  return r.bucket;
}

const N_PRESETS = [1_000, 5_000];

export function TimeRangeSelector({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (v: TimeRange) => void;
}) {
  const active = rangeKey(value);
  const [customN, setCustomN] = useState("");

  function pick(k: RangeKey) {
    if (k === "all") onChange({ kind: "all" });
    else if (k === "lastN") onChange({ kind: "lastN", messages: 1_000 });
    else onChange({ kind: "window", bucket: k });
  }

  const currentN = value.kind === "lastN" ? value.messages : null;

  return (
    <div className="range-block">
      <div className="toggle-row" role="group" aria-label="Time range">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className="chip-toggle"
            aria-pressed={active === r.key}
            onClick={() => pick(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>
      {active === "lastN" && (
        <div className="range-n">
          {N_PRESETS.map((n) => (
            <button
              key={n}
              type="button"
              className="chip-toggle chip-sm"
              aria-pressed={currentN === n}
              onClick={() => onChange({ kind: "lastN", messages: n })}
            >
              {n.toLocaleString("en-US")}
            </button>
          ))}
          <input
            className="input range-input"
            type="number"
            min={1}
            inputMode="numeric"
            placeholder="custom"
            value={customN}
            onChange={(e) => {
              setCustomN(e.target.value);
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n > 0) onChange({ kind: "lastN", messages: n });
            }}
            aria-label="Custom recent message count"
          />
        </div>
      )}
    </div>
  );
}
